import videojs from 'video.js';
import { formatDurationHeroMeta } from '../lib/duration-format';
import { extractYoutubeId } from '../lib/media-url';
import {
  SHOWCASE_HERO_BADGE_LABEL_MAX_CHARS,
  SHOWCASE_HERO_LEAD_MAX_CHARS,
  SHOWCASE_HERO_TITLE_MAX_CHARS,
  truncateEnd,
} from '../lib/truncate';
import { initFcMediaEmbedUrls } from './fc-media-embed';
import { syncVideoShowcaseOverlayPlayback } from './fc-media-start-overlay';

/** Spójnie z `verdictLabel` w video-showcase.ftl */
const VERDICT_LABELS: Record<string, string> = {
  falsz: 'Fałsz w sieci',
  prawda: 'Prawda',
  manipulacja: 'Manipulacja',
  czesciowo: 'Częściowa prawda',
  brak_dowodow: 'Brak dowodów',
  sledztwo: 'Śledztwo',
  inne: 'Materiał',
};

type ShowcaseRoot = HTMLElement & {
  dataset: DOMStringMap & {
    fcShowcase?: string;
    fcShowcaseInitialized?: string;
  };
};

function disposeVideoJsIn(container: Element): void {
  container.querySelectorAll<HTMLElement>('.video-js').forEach((wrap) => {
    try {
      const p = videojs.getPlayer(wrap);
      if (p && !p.isDisposed()) {
        p.dispose();
      }
    } catch {
      /* noop */
    }
  });
}

export function initVideoShowcasePlaylist(root: ParentNode = document): void {
  root.querySelectorAll<ShowcaseRoot>('[data-fc-showcase]').forEach((showcase) => {
    if (showcase.dataset.fcShowcaseInitialized === '1') {
      return;
    }
    showcase.dataset.fcShowcaseInitialized = '1';

    const mainSlot = showcase.querySelector<HTMLElement>('[data-fc-showcase-main]');
    const embedSlot = showcase.querySelector<HTMLElement>('[data-fc-showcase-embed-slot]');
    if (!mainSlot || !embedSlot) {
      return;
    }

    const syncStepIndicator = (activeIndex: string | undefined): void => {
      if (!activeIndex) {
        return;
      }
      const currentEl = showcase.querySelector<HTMLElement>('[data-fc-showcase-step-current]');
      if (currentEl) {
        currentEl.textContent = activeIndex;
      }
      showcase.querySelectorAll<HTMLElement>('[data-fc-showcase-segment]').forEach((seg) => {
        const n = seg.dataset.fcShowcaseSegment;
        seg.classList.toggle('fc-video-showcase__segment--active', n === activeIndex);
      });
      const stepRoot = showcase.querySelector<HTMLElement>('[data-fc-showcase-step]');
      const total = stepRoot
        ?.querySelector<HTMLElement>('[data-fc-showcase-step-total]')
        ?.textContent?.trim();
      if (stepRoot && total) {
        stepRoot.setAttribute('aria-label', `Aktywne wideo ${activeIndex} z ${total}`);
      }
    };

    const syncHeroOverlay = (btn: HTMLElement): void => {
      const overlay = showcase.querySelector<HTMLElement>('[data-fc-showcase-overlay]');
      if (!overlay) {
        return;
      }

      const badgesRow = overlay.querySelector<HTMLElement>('[data-fc-showcase-overlay-badges]');
      const badgeIcon = overlay.querySelector<HTMLElement>(
        '[data-fc-showcase-overlay-verdict-icon]',
      );
      const badgePill = overlay.querySelector<HTMLElement>(
        '[data-fc-showcase-overlay-verdict-pill]',
      );
      const durationEl = overlay.querySelector<HTMLElement>('[data-fc-showcase-overlay-duration]');
      const titleEl = overlay.querySelector<HTMLElement>('[data-fc-showcase-overlay-title]');
      const leadEl = overlay.querySelector<HTMLElement>('[data-fc-showcase-overlay-lead]');

      const verdict = btn.dataset.fcShowcaseVerdict?.trim() ?? '';
      const durationRaw = btn.dataset.fcShowcaseDuration?.trim() ?? '';

      const titleFromCard = btn.querySelector<HTMLElement>('.fc-video-showcase__card-title');
      const leadFromCard = btn.querySelector<HTMLElement>('.fc-video-showcase__card-lead');

      const titleText = titleFromCard?.textContent?.trim() ?? '';
      const leadPlain = leadFromCard?.textContent?.trim() ?? '';

      const iconBase = 'fc-video-showcase__verdict-icon';
      const pillBase = 'fc-video-showcase__verdict-pill';

      const emptyRowClass = 'fc-video-showcase__verdict-row--empty';

      if (badgesRow && badgeIcon && badgePill) {
        if (!verdict) {
          badgesRow.classList.add(emptyRowClass);
          badgesRow.setAttribute('aria-hidden', 'true');
          badgesRow.removeAttribute('aria-label');
          badgeIcon.className = iconBase;
          badgePill.className = pillBase;
          badgePill.textContent = '';
        } else {
          badgesRow.classList.remove(emptyRowClass);
          badgesRow.removeAttribute('aria-hidden');
          const label = VERDICT_LABELS[verdict] ?? verdict;
          badgesRow.setAttribute('aria-label', label);
          badgeIcon.className = `${iconBase} ${iconBase}--${verdict}`;
          badgePill.className = `${pillBase} ${pillBase}--${verdict}`;
          badgePill.textContent = truncateEnd(label, SHOWCASE_HERO_BADGE_LABEL_MAX_CHARS);
        }
      }

      if (durationEl) {
        if (!durationRaw) {
          durationEl.hidden = true;
          durationEl.textContent = '';
        } else {
          durationEl.hidden = false;
          durationEl.textContent = formatDurationHeroMeta(durationRaw);
        }
      }

      if (titleEl) {
        if (!titleText) {
          titleEl.hidden = true;
          titleEl.textContent = '';
        } else {
          titleEl.hidden = false;
          titleEl.textContent = truncateEnd(titleText, SHOWCASE_HERO_TITLE_MAX_CHARS);
        }
      }

      if (leadEl) {
        if (!leadPlain) {
          leadEl.hidden = true;
          leadEl.replaceChildren();
        } else {
          leadEl.hidden = false;
          leadEl.replaceChildren();
          const p = document.createElement('p');
          p.textContent = truncateEnd(leadPlain, SHOWCASE_HERO_LEAD_MAX_CHARS);
          leadEl.appendChild(p);
        }
      }
    };

    const activateShowcaseItem = (btn: HTMLElement): void => {
      const url = btn.dataset.fcShowcaseUrl?.trim();
      if (!url) {
        return;
      }

      syncVideoShowcaseOverlayPlayback(embedSlot, false);

      const idx = btn.dataset.fcShowcaseIndex;
      syncStepIndicator(idx);

      showcase.querySelectorAll<HTMLElement>('[data-fc-showcase-item]').forEach((el) => {
        el.removeAttribute('aria-current');
      });
      btn.setAttribute('aria-current', 'true');

      disposeVideoJsIn(embedSlot);
      embedSlot.replaceChildren();
      mainSlot.dataset.fcShowcaseActiveUrl = url;

      const mainEmbed = document.createElement('div');
      mainEmbed.className = 'fc-video-showcase__main-embed';

      const ytChromeMaskEl = document.createElement('div');
      ytChromeMaskEl.className = 'fc-video-showcase__yt-top-mask';
      ytChromeMaskEl.setAttribute('data-fc-showcase-yt-chrome-mask', '');
      ytChromeMaskEl.setAttribute('aria-hidden', 'true');
      mainEmbed.appendChild(ytChromeMaskEl);

      mainEmbed.dataset.fcMediaEmbedUrl = url;
      const kind = btn.dataset.fcShowcaseKind?.trim() ?? '';
      if (kind) {
        mainEmbed.dataset.fcMediaEmbedKind = kind;
      }
      embedSlot.appendChild(mainEmbed);
      initFcMediaEmbedUrls(embedSlot);

      syncHeroOverlay(btn);

      const isYoutube = kind === 'youtube' || Boolean(extractYoutubeId(url));
      const isFile = kind === 'file';
      ytChromeMaskEl.hidden = !(isYoutube || isFile);
    };

    showcase.querySelectorAll<HTMLElement>('[data-fc-showcase-item]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('a')) {
          return;
        }
        activateShowcaseItem(btn);
      });

      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activateShowcaseItem(btn);
        }
      });
    });
  });
}
